import type { AdminPriceInsight } from "@/types/price-insight";
import { evaluateResubmitEligibility } from "@/lib/listing-review-rounds/resubmitEligibility";
import { getPresetEvidenceRules } from "@/lib/listing-review-rounds/presetEvidence";
import type {
  ItemReviewPriceEscapeHatch,
  ItemReviewPriceOption,
  ItemReviewRoundPhase,
  ListingReviewSnapshot,
} from "@/lib/listing-review-rounds/types";
import { isSellerPriceDecisionStatus } from "@/lib/listing-review-rounds/reviewStatusSets";
import {
  computeBandRecommendationPrices,
  getOrderedBandPriceChips,
} from "@/lib/pricing/sellerPriceBandRecommendations";
import {
  getSellerBandChipTierLabel,
  getSellerBandChipTierSubtitle,
} from "@/lib/pricingInsightCopy";

export const PRICING_ISSUES_SLUG = "pricing_issues";

const LISTING_PRICE_MIN_NGN = 20_000;
const LISTING_PRICE_MAX_NGN = 3_000_000;

export type PriceOptionsValidationResult =
  | { ok: true; options: ItemReviewPriceOption[] }
  | { ok: false; error: string };

export type PriceEscapeHatchValidationResult =
  | { ok: true; hatch: ItemReviewPriceEscapeHatch | null }
  | { ok: false; error: string };

export function hasPricingIssuesPreset(reasonCodes: string[]): boolean {
  return reasonCodes.includes(PRICING_ISSUES_SLUG);
}

export function hasNonPricePresets(reasonCodes: string[]): boolean {
  return getPresetEvidenceRules(reasonCodes).some(
    (rule) => rule.kind !== "price",
  );
}

/**
 * Price-only admin feedback: accept may auto-approve.
 * Mixed (photos/text + price): accept records the choice for admin; does not approve.
 */
export function sellerPriceAcceptAutoApproves(reasonCodes: string[]): boolean {
  return (
    hasPricingIssuesPreset(reasonCodes) && !hasNonPricePresets(reasonCodes)
  );
}

/**
 * Initial round phase when admin sends feedback.
 * Price-only → seller can decide immediately; mixed → non-price first.
 */
export function resolveInitialReviewPhase(
  reasonCodes: string[],
): ItemReviewRoundPhase {
  if (hasPricingIssuesPreset(reasonCodes) && !hasNonPricePresets(reasonCodes)) {
    return "price_decision";
  }
  return "non_price";
}

function parsePositivePriceNgn(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    const rounded = Math.round(value);
    return rounded > 0 ? rounded : null;
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value.replace(/,/g, "").trim());
    if (Number.isFinite(parsed) && parsed > 0) {
      return Math.round(parsed);
    }
  }
  return null;
}

function optionalTrimmedString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function parsePriceOptionsFromJson(
  raw: unknown,
): ItemReviewPriceOption[] | null {
  if (raw === null || raw === undefined) return null;
  if (!Array.isArray(raw)) return null;
  const options: ItemReviewPriceOption[] = [];
  for (const entry of raw) {
    if (typeof entry !== "object" || entry === null || Array.isArray(entry)) {
      continue;
    }
    const o = entry as Record<string, unknown>;
    const amount = parsePositivePriceNgn(o.amount_ngn);
    if (amount === null) continue;
    options.push({
      amount_ngn: amount,
      label: optionalTrimmedString(o.label),
      explanation: optionalTrimmedString(o.explanation),
    });
  }
  return options.length > 0 ? options : null;
}

export function parsePriceEscapeHatchFromJson(
  raw: unknown,
): ItemReviewPriceEscapeHatch | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const enabled = o.enabled === true;
  if (!enabled) return null;
  const bandMin = parsePositivePriceNgn(o.band_min_ngn);
  const bandMax = parsePositivePriceNgn(o.band_max_ngn);
  if (bandMin === null || bandMax === null) return null;
  return {
    enabled: true,
    band_min_ngn: bandMin,
    band_max_ngn: bandMax,
    counter_used: o.counter_used === true,
  };
}

export function validatePriceOptionsInput(
  raw: unknown,
  options?: { required?: boolean },
): PriceOptionsValidationResult {
  if (raw === null || raw === undefined || raw === "") {
    if (options?.required) {
      return { ok: false, error: "Add at least one price option." };
    }
    return { ok: true, options: [] };
  }
  if (!Array.isArray(raw)) {
    return { ok: false, error: "price_options must be an array." };
  }
  if (raw.length === 0) {
    if (options?.required) {
      return { ok: false, error: "Add at least one price option." };
    }
    return { ok: true, options: [] };
  }
  if (raw.length > 3) {
    return { ok: false, error: "At most three price options are allowed." };
  }

  const optionsOut: ItemReviewPriceOption[] = [];
  for (let i = 0; i < raw.length; i += 1) {
    const entry = raw[i];
    if (typeof entry !== "object" || entry === null || Array.isArray(entry)) {
      return { ok: false, error: `Price option ${i + 1} is invalid.` };
    }
    const o = entry as Record<string, unknown>;
    const amount = parsePositivePriceNgn(o.amount_ngn);
    if (amount === null) {
      return {
        ok: false,
        error: `Price option ${i + 1} needs a valid amount in naira.`,
      };
    }
    if (amount < LISTING_PRICE_MIN_NGN || amount > LISTING_PRICE_MAX_NGN) {
      return {
        ok: false,
        error: `Price option ${i + 1} must be between ₦${LISTING_PRICE_MIN_NGN.toLocaleString()} and ₦${LISTING_PRICE_MAX_NGN.toLocaleString()}.`,
      };
    }
    optionsOut.push({
      amount_ngn: amount,
      label: optionalTrimmedString(o.label),
      explanation: optionalTrimmedString(o.explanation),
    });
  }

  return { ok: true, options: optionsOut };
}

export function validatePriceEscapeHatchInput(
  raw: unknown,
  priceOptions: ItemReviewPriceOption[],
): PriceEscapeHatchValidationResult {
  if (raw === null || raw === undefined) {
    return { ok: true, hatch: null };
  }
  if (typeof raw !== "object" || Array.isArray(raw)) {
    return { ok: false, error: "price_escape_hatch must be an object." };
  }
  const o = raw as Record<string, unknown>;
  if (o.enabled !== true) {
    return { ok: true, hatch: null };
  }

  const bandMin = parsePositivePriceNgn(o.band_min_ngn);
  const bandMax = parsePositivePriceNgn(o.band_max_ngn);
  if (bandMin === null || bandMax === null) {
    return {
      ok: false,
      error: "Escape hatch requires a valid min and max price band.",
    };
  }
  if (bandMin > bandMax) {
    return {
      ok: false,
      error: "Escape hatch min price cannot exceed max price.",
    };
  }
  if (bandMin < LISTING_PRICE_MIN_NGN || bandMax > LISTING_PRICE_MAX_NGN) {
    return {
      ok: false,
      error: `Escape hatch band must stay within ₦${LISTING_PRICE_MIN_NGN.toLocaleString()}–₦${LISTING_PRICE_MAX_NGN.toLocaleString()}.`,
    };
  }

  if (priceOptions.length > 0) {
    const optionAmounts = priceOptions.map((opt) => opt.amount_ngn);
    const lowestOption = Math.min(...optionAmounts);
    const highestOption = Math.max(...optionAmounts);
    if (bandMax < lowestOption * 0.5 || bandMin > highestOption * 1.5) {
      // Soft sanity only — allow admin override; no hard block.
    }
  }

  return {
    ok: true,
    hatch: {
      enabled: true,
      band_min_ngn: bandMin,
      band_max_ngn: bandMax,
      counter_used: false,
    },
  };
}

/** Optional prefill from pricing insight — admin must confirm before send.
 * Prices + labels match `/sell` band chips (Standard / Sell sooner / Sell fastest).
 */
export function buildPriceOptionsFromInsight(
  insight: AdminPriceInsight,
): ItemReviewPriceOption[] {
  const bandMin = insight.bandMin ?? insight.priceRange.minPrice;
  const bandMax = insight.bandMax ?? insight.priceRange.maxPrice;
  if (
    !Number.isFinite(bandMin) ||
    !Number.isFinite(bandMax) ||
    bandMax < bandMin
  ) {
    return [];
  }

  const chips = getOrderedBandPriceChips(
    computeBandRecommendationPrices({ min: bandMin, max: bandMax }),
  );

  const seenPrices = new Set<number>();
  const options: ItemReviewPriceOption[] = [];
  for (const chip of chips) {
    if (seenPrices.has(chip.price)) continue;
    seenPrices.add(chip.price);
    options.push({
      amount_ngn: chip.price,
      label: getSellerBandChipTierLabel(chip.tier),
      explanation: getSellerBandChipTierSubtitle(chip.tier),
    });
    if (options.length >= 3) break;
  }
  return options;
}

export function nonPriceEvidenceComplete(input: {
  reasonCodes: string[];
  satisfiedPresets?: string[];
  baselineSnapshot: ListingReviewSnapshot | null;
  currentSnapshot: ListingReviewSnapshot | null;
}): boolean {
  if (!hasNonPricePresets(input.reasonCodes)) {
    return true;
  }
  if (!input.baselineSnapshot || !input.currentSnapshot) {
    return false;
  }
  const eligibility = evaluateResubmitEligibility({
    reasonCodes: input.reasonCodes,
    satisfiedPresets: input.satisfiedPresets,
    baselineSnapshot: input.baselineSnapshot,
    currentSnapshot: input.currentSnapshot,
  });
  return eligibility.allowed;
}

/**
 * Whether seller may see/act on price options (Phase 5 actions; Phase 4 visibility).
 */
export function isPriceDecisionUnlocked(input: {
  reasonCodes: string[];
  reviewPhase?: ItemReviewRoundPhase | null;
  priceOptions?: ItemReviewPriceOption[] | null;
  satisfiedPresets?: string[];
  baselineSnapshot?: ListingReviewSnapshot | null;
  currentSnapshot?: ListingReviewSnapshot | null;
}): boolean {
  if (!hasPricingIssuesPreset(input.reasonCodes)) {
    return false;
  }
  if (!input.priceOptions?.length) {
    return false;
  }
  if (input.reviewPhase === "price_decision") {
    return true;
  }
  if (!hasNonPricePresets(input.reasonCodes)) {
    return true;
  }
  return nonPriceEvidenceComplete({
    reasonCodes: input.reasonCodes,
    satisfiedPresets: input.satisfiedPresets,
    baselineSnapshot: input.baselineSnapshot ?? null,
    currentSnapshot: input.currentSnapshot ?? null,
  });
}

export function sellerPricePhaseLockedMessage(): string {
  return "Pricing options will appear after you address the other requested changes.";
}

export function formatPriceOptionPreviewLine(
  option: ItemReviewPriceOption,
  index: number,
): string {
  const amount = `₦${option.amount_ngn.toLocaleString("en-NG")}`;
  const label = option.label?.trim();
  const head = label
    ? `Option ${index + 1} — ${label} · ${amount}`
    : `Option ${index + 1} · ${amount}`;
  if (option.explanation?.trim()) {
    return `${head}\n${option.explanation.trim()}`;
  }
  return head;
}

/** Admin modal preview — phased seller view. */
export function formatPhasedAdminReviewPreview(input: {
  presetBullets: string[];
  reasonCodes: string[];
  reviewPhase: ItemReviewRoundPhase;
  priceOptions: ItemReviewPriceOption[];
  adminNote?: string | null;
  priceUnlockedForPreview?: boolean;
}): string {
  const lines: string[] = [];
  const nonPriceBullets = input.presetBullets.filter((line) => {
    const lower = line.toLowerCase();
    return !lower.includes("pricing") && !lower.includes("price");
  });

  if (nonPriceBullets.length > 0) {
    lines.push("Fix first:");
    for (const bullet of nonPriceBullets) {
      lines.push(`• ${bullet.replace(/^\*\s*/, "").trim()}`);
    }
  }

  if (hasPricingIssuesPreset(input.reasonCodes)) {
    const unlocked =
      input.priceUnlockedForPreview ??
      (input.reviewPhase === "price_decision" ||
        !hasNonPricePresets(input.reasonCodes));

    if (unlocked && input.priceOptions.length > 0) {
      lines.push("");
      lines.push("Pricing — choose one:");
      input.priceOptions.forEach((opt, i) => {
        lines.push(
          `• ${formatPriceOptionPreviewLine(opt, i).replace(/\n/g, " — ")}`,
        );
      });
    } else {
      lines.push("");
      lines.push(`Pricing — ${sellerPricePhaseLockedMessage()}`);
    }
  }

  const note = input.adminNote?.trim();
  if (note) {
    lines.push("");
    lines.push(`Note: ${note}`);
  }

  return lines.join("\n").trim();
}

/**
 * Whether the seller has an actionable price decision pending.
 * Pure / client-safe — no server imports.
 */
export function sellerPriceDecisionPending(
  status: string | null | undefined,
  feedback:
    | {
        reasonCodes: string[];
        priceOptions?: { amount_ngn: number }[] | null;
        reviewPhase?: string | null;
        satisfiedPresets?: string[];
        priceSellerDecision?: { action?: string } | null;
      }
    | null
    | undefined,
): boolean {
  if (!isSellerPriceDecisionStatus(status)) {
    return false;
  }
  if (!feedback || !hasPricingIssuesPreset(feedback.reasonCodes)) {
    return false;
  }
  if (!feedback.priceOptions?.length) {
    return false;
  }
  // Mixed accept already recorded — no further seller price action.
  if (feedback.priceSellerDecision?.action === "accept") {
    return false;
  }
  return isPriceDecisionUnlocked({
    reasonCodes: feedback.reasonCodes,
    reviewPhase: (feedback.reviewPhase as ItemReviewRoundPhase) ?? null,
    priceOptions: feedback.priceOptions,
    satisfiedPresets: feedback.satisfiedPresets,
  });
}
